/**
 * Settings > Desk. When the desk is open (business hours per weekday and the
 * time zone), which priorities count every minute, and the vendor mailbox the
 * desk watches and sends from.
 */

import { useEffect, useState } from 'react';
import type { SdPriority } from '@ampersandbrands/ui';
import { FilterLabel } from '@/components/FilterLabel';
import { useDeskConfig, usePatchDeskConfig } from '@/lib/useServiceDesk';
import { SD_WEEKDAYS, type SdDayHours, type SdDeskConfig, type SdWeekday } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { ALL_PRIORITIES, PRIORITY_LABELS } from './sdLabels';
import { SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

const DAY_NAMES: Record<SdWeekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

const ZONES = [
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'UTC',
];

export function DeskTab() {
  const q = useDeskConfig();
  const patch = usePatchDeskConfig();
  const [hours, setHours] = useState<Partial<Record<SdWeekday, SdDayHours>>>({});
  const [zone, setZone] = useState('America/Denver');
  const [always, setAlways] = useState<SdPriority[]>([]);
  const [mailbox, setMailbox] = useState('');
  const [sender, setSender] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const d = q.data;
    if (!d) return;
    setHours(d.business_hours ?? {});
    setZone(d.timezone);
    setAlways(d.sla_247_priorities ?? []);
    setMailbox(d.vendor_mailbox ?? '');
    setSender(d.sender ?? '');
  }, [q.data]);

  const edit = (day: SdWeekday, fn: (cur: SdDayHours) => SdDayHours) => {
    setSaved(false);
    setHours((cur) => ({ ...cur, [day]: fn(cur[day] ?? []) }));
  };

  const zoneOptions = ZONES.includes(zone) ? ZONES : [zone, ...ZONES];

  return (
    <div className={SD_CARD}>
      {q.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading desk settings...</p>
      ) : (
        <div className="flex max-w-2xl flex-col gap-5">
          <div>
            <FilterLabel as="div">Business hours</FilterLabel>
            <ul className="flex flex-col gap-2">
              {SD_WEEKDAYS.map((day) => {
                const ranges = hours[day] ?? [];
                const open = ranges.length > 0;
                const first = ranges[0] ?? ['08:00', '17:00'];
                return (
                  <li key={day} className="flex flex-wrap items-center gap-3 text-[13px] text-amp-ink dark:text-amp-dark-ink">
                    <label className="flex w-36 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={open}
                        onChange={(e) =>
                          edit(day, (cur) => (e.target.checked ? (cur.length > 0 ? cur : [['08:00', '17:00']]) : []))
                        }
                      />
                      {DAY_NAMES[day]}
                    </label>
                    {open ? (
                      <>
                        <input
                          type="time"
                          aria-label={`${DAY_NAMES[day]} opens`}
                          value={first[0]}
                          onChange={(e) => edit(day, (cur) => [[e.target.value, cur[0]?.[1] ?? '17:00'], ...cur.slice(1)])}
                          className={`${SD_INPUT} w-32`}
                        />
                        <span className={SD_MUTED}>to</span>
                        <input
                          type="time"
                          aria-label={`${DAY_NAMES[day]} closes`}
                          value={first[1]}
                          onChange={(e) => edit(day, (cur) => [[cur[0]?.[0] ?? '08:00', e.target.value], ...cur.slice(1)])}
                          className={`${SD_INPUT} w-32`}
                        />
                        {ranges.length > 1 && <span className={SD_MUTED}>(plus {ranges.length - 1} more range, kept as is)</span>}
                      </>
                    ) : (
                      <span className={SD_MUTED}>Closed</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <FilterLabel htmlFor="sd-zone">Time zone</FilterLabel>
            <select
              id="sd-zone"
              value={zone}
              onChange={(e) => {
                setZone(e.target.value);
                setSaved(false);
              }}
              className={`${SD_INPUT} max-w-xs`}
            >
              {zoneOptions.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </div>

          <div>
            <FilterLabel as="div">Priorities that count every minute, day and night</FilterLabel>
            <div className="flex flex-wrap gap-4 text-[13px] text-amp-ink dark:text-amp-dark-ink">
              {ALL_PRIORITIES.map((p) => (
                <label key={p} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={always.includes(p)}
                    onChange={() => {
                      setSaved(false);
                      setAlways((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));
                    }}
                  />
                  {PRIORITY_LABELS[p]}
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FilterLabel htmlFor="sd-vmail">Vendor mailbox the desk watches</FilterLabel>
              <input
                id="sd-vmail"
                value={mailbox}
                onChange={(e) => {
                  setMailbox(e.target.value);
                  setSaved(false);
                }}
                placeholder="Leave blank to turn vendor email off"
                className={SD_INPUT}
              />
            </div>
            <div>
              <FilterLabel htmlFor="sd-sender">Email sender for this desk</FilterLabel>
              <input
                id="sd-sender"
                value={sender}
                onChange={(e) => {
                  setSender(e.target.value);
                  setSaved(false);
                }}
                className={SD_INPUT}
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={patch.isPending}
              className={SD_BTN_PRIMARY}
              onClick={() => {
                const body: Partial<SdDeskConfig> = {
                  business_hours: hours,
                  timezone: zone,
                  sla_247_priorities: always,
                  vendor_mailbox: mailbox.trim() || null,
                  sender: sender.trim() || null,
                };
                patch.mutate(body, { onSuccess: () => setSaved(true) });
              }}
            >
              Save desk settings
            </button>
            {saved && <span className={`text-[13px] ${SD_MUTED}`}>Saved.</span>}
          </div>
          <SdError error={q.error ?? patch.error} what="save the desk settings" />
        </div>
      )}
    </div>
  );
}
